import { Grid, makeStyles } from '@material-ui/core'
import SelectInput from 'components/common/SelectInput'
import { TxTrackingListTypes } from 'consts/txTrackingReportConsts'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/analyticsUtils'
import TxTrackingCategoryChip from './TxTrackingCategoryChip'
import ViewSelectorCaretIcon from './ViewSelectorCaretIcon'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  // Figma frame 220 sets the view title 24px from the card edge on a 37px baseline, the chips 57px down.
  titleContainer: {
    marginLeft: -10,
    marginTop: -8.4,
    paddingBottom: 5.4
  },
  dropdown: {
    borderRadius: 16
  },
  selectedValue: {
    '&&': {
      fontSize: 16,
      fontWeight: 400
    }
  },
  selectOption: {
    fontSize: 15,
    padding: '10px 20px'
  },
  // Figma frame 220 starts the chips 11px left of the view title and keeps 12px below them.
  chips: {
    marginLeft: -7,
    marginBottom: -8,
    rowGap: 6
  }
}))

const TxTrackingListHeader = ({
  listType,
  onListTypeSelected,
  categories = [],
  viewTotal = 0,
  selectedCategories,
  setSelectedCategories,
  pastXDays
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const titleOptions = Object.values(TxTrackingListTypes).map(type => ({
    label: t(`pages.txTrackingReport.patientsList.views.${type}`),
    value: type
  }))

  const handleCategoryClicked = useCallback(
    categoryKey => {
      const updatedCategories = selectedCategories.includes(categoryKey)
        ? selectedCategories.filter(selectedCategory => selectedCategory !== categoryKey)
        : [...selectedCategories, categoryKey]

      trackEvent('Tx Tracking Report - Category clicked', {
        selectedCategories: updatedCategories,
        view: listType,
        pastXDays
      })
      setSelectedCategories(updatedCategories)
    },
    [selectedCategories, setSelectedCategories, listType, pastXDays]
  )

  const handleAllClicked = useCallback(() => {
    trackEvent('Tx Tracking Report - Category clicked', {
      selectedCategories: [],
      view: listType,
      pastXDays
    })
    setSelectedCategories([])
  }, [setSelectedCategories, listType, pastXDays])

  const getCategoryLabel = categoryKey =>
    listType === TxTrackingListTypes.TrackingReasons
      ? t(`dialogs.scanSummary.treatmentTracking.conditions.${categoryKey}`)
      : t(`pages.txTrackingReport.patientsList.categories.${categoryKey}`)

  return (
    <Grid container className={classes.root}>
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item className={classes.titleContainer}>
          <SelectInput
            selectStyle={{ border: 'none', fontSize: 16, fontWeight: 400, marginTop: -2 }}
            style={{ bright: true, thick: true }}
            value={listType}
            options={titleOptions}
            keepErrorContainerWhenInactive={false}
            dropdownClassName={classes.dropdown}
            valueClassName={classes.selectedValue}
            optionClassName={classes.selectOption}
            arrowIconComponent={ViewSelectorCaretIcon}
            onChange={({ value }) => onListTypeSelected(value)}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.chips}>
        <TxTrackingCategoryChip
          label={t('pages.txTrackingReport.patientsList.categories.all')}
          count={viewTotal}
          selected={selectedCategories.length === 0}
          onClick={handleAllClicked}
        />
        {categories.map(category => (
          <TxTrackingCategoryChip
            key={category.key}
            label={getCategoryLabel(category.key)}
            count={category.count}
            color={category.color}
            selected={selectedCategories.includes(category.key)}
            disabled={!category.count}
            onClick={() => handleCategoryClicked(category.key)}
          />
        ))}
      </Grid>
    </Grid>
  )
}

export default TxTrackingListHeader
